import { motion } from "framer-motion";
import { ShieldCheck, TrendingUp, Coins, Receipt } from "lucide-react";

const pillars = [
  {
    n: "01",
    icon: ShieldCheck,
    title: "Capital Preservation",
    body: "Protect the base first. Downside control, emergency liquidity and risk limits so you never sell at the worst possible time.",
  },
  {
    n: "02",
    icon: TrendingUp,
    title: "Capital Appreciation",
    body: "Grow the corpus. Quality equities and disciplined SIPs that compound above inflation across full market cycles.",
  },
  {
    n: "03",
    icon: Coins,
    title: "Cashflow",
    body: "Engineer income. Dividends, coupons and option premium that pay you predictably — the engine of the kingdom.",
  },
  {
    n: "04",
    icon: Receipt,
    title: "Tax Efficiency",
    body: "Keep more of it. Structure holdings, harvest losses and use the right wrappers so returns compound after tax, not before.",
  },
];

export default function FourPillars() {
  return (
    <section id="pillars" data-testid="four-pillars" className="relative z-10 py-24 md:py-32 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="max-w-2xl mb-16">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-primary mb-5">/ The Foundation</p>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
            Four pillars of a<br />good cashflow system
          </h2>
          <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed">
            Every model we build stands on four load-bearing pillars. Neglect one and the
            structure leans — balance all four and wealth becomes durable.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {pillars.map((p, i) => (
            <motion.div
              key={p.n}
              data-testid={`pillar-${p.n}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="group relative bg-background p-8 lg:p-9 hover:bg-card transition-colors"
            >
              <div className="absolute top-0 left-0 h-[3px] w-0 bg-primary group-hover:w-full transition-[width] duration-500 ease-out" />
              <span className="font-mono-accent text-xs tracking-[0.2em] text-muted-foreground">{p.n}</span>
              <div className="w-12 h-12 border border-border flex items-center justify-center my-6 group-hover:border-primary transition-colors">
                <p.icon size={22} className="text-primary" />
              </div>
              <h3 className="font-display font-extrabold uppercase tracking-tight text-xl sm:text-2xl mb-3 leading-tight group-hover:text-primary transition-colors">
                {p.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
